import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { DashboardhMediumButton } from 'bd-web-components'

import {
  EXTRA_QUESTION_RULES,
  EventExtraQuestion,
  ExtraQuestionAnswerType,
  SaveEventExtraQuestionPayload,
} from '../../hooks/reviews'
import Loader from '../../components/Loader'
import { getDashColor } from '../../styles'
import {
  AddOptionButton,
  ChoiceGroup,
  ChoiceLabel,
  EmptyState,
  ExceptionContainer,
  FieldCounter,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FormActions,
  IconTextButton,
  InactiveBadge,
  LockedNotice,
  OptionField,
  OptionIndex,
  OptionResultBar,
  OptionResultFill,
  OptionResultHeader,
  OptionResultItem,
  OptionResultLabel,
  OptionResultsList,
  OptionResultsNote,
  OptionResultValue,
  OptionRow,
  OptionsList,
  PageOptions,
  QuestionCard,
  QuestionSummary,
  QuestionSummaryText,
  QuestionTitle,
  ReviewActions,
  ReviewMeta,
  SectionDescription,
  SectionHeaderRow,
  SettingsGrid,
  SwitchLabel,
  SwitchTrack,
  TextInput,
} from './styles'

const { questionMaxLength, optionMaxLength, minOptions, maxOptions } =
  EXTRA_QUESTION_RULES

const ANSWER_TYPE_OPTIONS: { key: ExtraQuestionAnswerType; label: string }[] =
  [
    { key: 'single', label: 'Escolha única' },
    { key: 'multiple', label: 'Múltipla escolha' },
  ]

// Exemplo genérico: nunca citar o nome de um evento específico aqui.
const EXAMPLE_QUESTION = 'Como você ficou sabendo deste evento?'
const EXAMPLE_OPTIONS = [
  'Redes sociais',
  'Indicação de amigo ou familiar',
  'Pesquisa na internet',
  'Outro',
]

interface FormState {
  question: string
  options: string[]
  answerType: ExtraQuestionAnswerType
  required: boolean
  active: boolean
}

interface FormErrors {
  question?: string
  options?: string
  optionItems: (string | undefined)[]
}

const emptyForm = (): FormState => ({
  question: '',
  options: Array.from({ length: minOptions }, () => ''),
  answerType: 'single',
  required: false,
  active: true,
})

function toForm(question: EventExtraQuestion): FormState {
  return {
    question: question.question,
    options: question.options.map((option) => option.label),
    answerType: question.answerType,
    required: question.required,
    active: question.active,
  }
}

function normalizeOption(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR')
}

function validate(form: FormState): FormErrors {
  const errors: FormErrors = { optionItems: [] }

  const question = form.question.trim()
  if (!question) {
    errors.question = 'O texto da pergunta é obrigatório.'
  } else if (question.length > questionMaxLength) {
    errors.question = `A pergunta deve ter no máximo ${questionMaxLength} caracteres.`
  }

  if (form.options.length < minOptions) {
    errors.options = `Informe no mínimo ${minOptions} opções.`
  } else if (form.options.length > maxOptions) {
    errors.options = `Informe no máximo ${maxOptions} opções.`
  }

  const seen = new Map<string, number>()
  form.options.forEach((option, index) => {
    const trimmed = option.trim()
    if (!trimmed) {
      errors.optionItems[index] = 'Preencha o texto da opção.'
      return
    }
    if (trimmed.length > optionMaxLength) {
      errors.optionItems[index] = `Máximo de ${optionMaxLength} caracteres.`
      return
    }
    const key = normalizeOption(trimmed)
    const firstIndex = seen.get(key)
    if (firstIndex !== undefined) {
      errors.optionItems[index] = `Opção duplicada (igual à opção ${
        firstIndex + 1
      }).`
      return
    }
    seen.set(key, index)
  })

  return errors
}

function hasErrors(errors: FormErrors) {
  return (
    Boolean(errors.question) ||
    Boolean(errors.options) ||
    errors.optionItems.some(Boolean)
  )
}

interface ExtraQuestionSectionProps {
  eventId: string | undefined
  getEventExtraQuestion: () => Promise<EventExtraQuestion | null>
  saveEventExtraQuestion: (
    payload: SaveEventExtraQuestionPayload,
    idEventExtraQuestion?: string
  ) => Promise<EventExtraQuestion>
  setEventExtraQuestionActive: (
    idEventExtraQuestion: string,
    active: boolean
  ) => Promise<unknown>
}

export function ExtraQuestionSection({
  eventId,
  getEventExtraQuestion,
  saveEventExtraQuestion,
  setEventExtraQuestionActive,
}: ExtraQuestionSectionProps) {
  const [question, setQuestion] = useState<EventExtraQuestion | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isTogglingActive, setIsTogglingActive] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [submitted, setSubmitted] = useState(false)

  const isLocked = Boolean(question && question.answersCount > 0)

  const errors = useMemo(() => validate(form), [form])
  const showErrors = submitted

  const load = useCallback(async () => {
    if (!eventId) return
    try {
      setIsLoading(true)
      setLoadError(false)
      const result = await getEventExtraQuestion()
      setQuestion(result)
    } catch (error) {
      setLoadError(true)
      toast.error('Um erro aconteceu ao carregar a pergunta extra')
    } finally {
      setIsLoading(false)
    }
  }, [eventId, getEventExtraQuestion])

  useEffect(() => {
    load()
  }, [load])

  const startCreate = () => {
    setForm(emptyForm())
    setSubmitted(false)
    setIsEditing(true)
  }

  const startEdit = () => {
    if (!question) return
    setForm(toForm(question))
    setSubmitted(false)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setSubmitted(false)
    setForm(emptyForm())
  }

  const fillExample = () => {
    setForm((prev) => ({
      ...prev,
      question: EXAMPLE_QUESTION,
      options: [...EXAMPLE_OPTIONS],
    }))
  }

  const updateOption = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((option, i) => (i === index ? value : option)),
    }))
  }

  const addOption = () => {
    setForm((prev) =>
      prev.options.length >= maxOptions
        ? prev
        : { ...prev, options: [...prev.options, ''] }
    )
  }

  const removeOption = (index: number) => {
    setForm((prev) =>
      prev.options.length <= minOptions
        ? prev
        : { ...prev, options: prev.options.filter((_, i) => i !== index) }
    )
  }

  const moveOption = (index: number, direction: -1 | 1) => {
    setForm((prev) => {
      const target = index + direction
      if (target < 0 || target >= prev.options.length) return prev
      const options = [...prev.options]
      ;[options[index], options[target]] = [options[target], options[index]]
      return { ...prev, options }
    })
  }

  const handleSave = async () => {
    setSubmitted(true)
    if (hasErrors(errors)) return

    const payload: SaveEventExtraQuestionPayload = {
      question: form.question.trim(),
      options: form.options.map((option) => option.trim()),
      answerType: form.answerType,
      required: form.required,
      active: form.active,
    }

    try {
      setIsSaving(true)
      await saveEventExtraQuestion(payload, question?.idEventExtraQuestion)
      toast.success('Pergunta extra salva com sucesso')
      setIsEditing(false)
      setSubmitted(false)
      await load()
    } catch (error) {
      toast.error('Um erro aconteceu ao salvar a pergunta extra')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async () => {
    if (!question) return
    const nextActive = !question.active
    try {
      setIsTogglingActive(true)
      await setEventExtraQuestionActive(question.idEventExtraQuestion, nextActive)
      toast.success(
        nextActive
          ? 'Pergunta extra ativada com sucesso'
          : 'Pergunta extra desativada com sucesso'
      )
      await load()
    } catch (error) {
      toast.error('Um erro aconteceu ao atualizar a pergunta extra')
    } finally {
      setIsTogglingActive(false)
    }
  }

  const answerTypeLabel = (type: ExtraQuestionAnswerType) =>
    ANSWER_TYPE_OPTIONS.find((option) => option.key === type)?.label ?? type

  const renderSummary = () => {
    if (!question) return null

    return (
      <QuestionCard>
        <QuestionSummary>
          <QuestionSummaryText>
            <QuestionTitle>
              {question.question}{' '}
              {!question.active && <InactiveBadge>Desativada</InactiveBadge>}
            </QuestionTitle>
            <ReviewMeta>
              {answerTypeLabel(question.answerType)} · Resposta{' '}
              {question.required ? 'obrigatória' : 'opcional'} ·{' '}
              {question.answersCount}{' '}
              {question.answersCount === 1 ? 'resposta' : 'respostas'}
            </ReviewMeta>
            <OptionResultsList>
              {question.options.map((option, index) => {
                const count = option.answersCount ?? 0
                const percent =
                  question.answersCount > 0
                    ? Math.round((count / question.answersCount) * 100)
                    : 0
                return (
                  <OptionResultItem
                    key={option.idOption ?? `${index}-${option.label}`}
                  >
                    <OptionResultHeader>
                      <OptionResultLabel>{option.label}</OptionResultLabel>
                      <OptionResultValue>
                        {count} {count === 1 ? 'resposta' : 'respostas'} ·{' '}
                        {percent}%
                      </OptionResultValue>
                    </OptionResultHeader>
                    <OptionResultBar>
                      <OptionResultFill $percent={percent} />
                    </OptionResultBar>
                  </OptionResultItem>
                )
              })}
            </OptionResultsList>
            {question.answerType === 'multiple' &&
              question.answersCount > 0 && (
                <OptionResultsNote>
                  Como o comprador pode marcar mais de uma opção, a soma dos
                  percentuais pode passar de 100%.
                </OptionResultsNote>
              )}
          </QuestionSummaryText>
          <ReviewActions>
            <DashboardhMediumButton
              onClick={startEdit}
              color="#fff"
              background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
              height="40px"
              maxWidth="100%"
              variant={isTogglingActive ? 'disabled' : 'contained'}
              width="120px"
              borderRadius="100px"
              disabled={isTogglingActive}
            >
              Editar
            </DashboardhMediumButton>
            <DashboardhMediumButton
              onClick={handleToggleActive}
              color="#fff"
              background={getDashColor(
                question.active ? 'PRIMARY_RED_01' : 'SECONDARY_GREEN_07',
                'GRAY_DARK'
              )}
              height="40px"
              maxWidth="100%"
              variant={isTogglingActive ? 'disabled' : 'contained'}
              width="120px"
              borderRadius="100px"
              disabled={isTogglingActive}
            >
              {isTogglingActive ? '...' : question.active ? 'Desativar' : 'Ativar'}
            </DashboardhMediumButton>
          </ReviewActions>
        </QuestionSummary>
        {isLocked && (
          <LockedNotice>
            Esta pergunta já recebeu respostas. O texto e as opções não podem
            mais ser alterados para não corromper a base de respostas, mas a
            pergunta pode ser desativada.
          </LockedNotice>
        )}
      </QuestionCard>
    )
  }

  const renderForm = () => {
    const questionLength = form.question.length
    const canAddOption = form.options.length < maxOptions && !isLocked
    const canRemoveOption = form.options.length > minOptions && !isLocked

    return (
      <QuestionCard>
        {isLocked && (
          <LockedNotice>
            Esta pergunta já recebeu respostas. O texto e as opções estão
            bloqueados para edição.
          </LockedNotice>
        )}

        <FieldGroup>
          <FieldLabelRow>
            <FieldLabel htmlFor="extra-question-text">Pergunta</FieldLabel>
            <FieldCounter $over={questionLength > questionMaxLength}>
              {questionLength}/{questionMaxLength}
            </FieldCounter>
          </FieldLabelRow>
          <TextInput
            id="extra-question-text"
            value={form.question}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, question: event.target.value }))
            }
            placeholder={`Ex.: ${EXAMPLE_QUESTION}`}
            maxLength={questionMaxLength}
            disabled={isLocked || isSaving}
            $error={showErrors && Boolean(errors.question)}
          />
          {showErrors && errors.question && (
            <FieldError>{errors.question}</FieldError>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabelRow>
            <FieldLabel>Opções de resposta</FieldLabel>
            <FieldCounter>
              {form.options.length}/{maxOptions} (mínimo {minOptions})
            </FieldCounter>
          </FieldLabelRow>
          <OptionsList>
            {form.options.map((option, index) => {
              const itemError = showErrors ? errors.optionItems[index] : undefined
              return (
                <FieldGroup key={index}>
                  <OptionRow>
                    <OptionIndex>{index + 1}</OptionIndex>
                    <OptionField>
                      <TextInput
                        value={option}
                        onChange={(event) =>
                          updateOption(index, event.target.value)
                        }
                        placeholder={`Opção ${index + 1}`}
                        maxLength={optionMaxLength}
                        disabled={isLocked || isSaving}
                        $error={Boolean(itemError)}
                        aria-label={`Opção ${index + 1}`}
                      />
                    </OptionField>
                    <IconTextButton
                      type="button"
                      onClick={() => moveOption(index, -1)}
                      disabled={isLocked || isSaving || index === 0}
                      aria-label="Mover opção para cima"
                      title="Mover para cima"
                    >
                      ↑
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={() => moveOption(index, 1)}
                      disabled={
                        isLocked || isSaving || index === form.options.length - 1
                      }
                      aria-label="Mover opção para baixo"
                      title="Mover para baixo"
                    >
                      ↓
                    </IconTextButton>
                    <IconTextButton
                      type="button"
                      onClick={() => removeOption(index)}
                      disabled={!canRemoveOption || isSaving}
                      aria-label="Remover opção"
                      title="Remover"
                    >
                      ×
                    </IconTextButton>
                  </OptionRow>
                  {itemError && <FieldError>{itemError}</FieldError>}
                </FieldGroup>
              )
            })}
          </OptionsList>
          {showErrors && errors.options && (
            <FieldError>{errors.options}</FieldError>
          )}
          <AddOptionButton
            type="button"
            onClick={addOption}
            disabled={!canAddOption || isSaving}
          >
            + Adicionar opção
          </AddOptionButton>
        </FieldGroup>

        <SettingsGrid>
          <FieldGroup>
            <FieldLabel>Tipo de resposta</FieldLabel>
            <ChoiceGroup>
              {ANSWER_TYPE_OPTIONS.map((option) => (
                <ChoiceLabel
                  key={option.key}
                  $active={form.answerType === option.key}
                  $disabled={isSaving}
                >
                  <input
                    type="radio"
                    name="extra-question-answer-type"
                    value={option.key}
                    checked={form.answerType === option.key}
                    disabled={isSaving}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, answerType: option.key }))
                    }
                  />
                  {option.label}
                </ChoiceLabel>
              ))}
            </ChoiceGroup>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Configurações</FieldLabel>
            <SwitchLabel $disabled={isSaving}>
              <input
                type="checkbox"
                checked={form.required}
                disabled={isSaving}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    required: event.target.checked,
                  }))
                }
              />
              <SwitchTrack $on={form.required} />
              Resposta obrigatória
            </SwitchLabel>
            <SwitchLabel $disabled={isSaving}>
              <input
                type="checkbox"
                checked={form.active}
                disabled={isSaving}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, active: event.target.checked }))
                }
              />
              <SwitchTrack $on={form.active} />
              Pergunta ativa
            </SwitchLabel>
          </FieldGroup>
        </SettingsGrid>

        <FormActions>
          {!isLocked && !question && (
            <DashboardhMediumButton
              onClick={fillExample}
              height="40px"
              maxWidth="100%"
              variant="outlined"
              width="180px"
              borderRadius="100px"
              disabled={isSaving}
            >
              Usar exemplo
            </DashboardhMediumButton>
          )}
          <DashboardhMediumButton
            onClick={cancelEdit}
            height="40px"
            maxWidth="100%"
            variant="outlined"
            width="120px"
            borderRadius="100px"
            disabled={isSaving}
          >
            Cancelar
          </DashboardhMediumButton>
          <DashboardhMediumButton
            onClick={handleSave}
            color="#fff"
            background={getDashColor('SECONDARY_GREEN_07', 'GRAY_DARK')}
            height="40px"
            maxWidth="100%"
            variant={isSaving ? 'disabled' : 'contained'}
            width="140px"
            borderRadius="100px"
            disabled={isSaving}
          >
            {isSaving ? <Loader small noMargin /> : 'Salvar'}
          </DashboardhMediumButton>
        </FormActions>
      </QuestionCard>
    )
  }

  return (
    <>
      <SectionHeaderRow>
        <PageOptions>Pergunta extra</PageOptions>
        {!isLoading && !loadError && !question && !isEditing && (
          <DashboardhMediumButton
            onClick={startCreate}
            color="#fff"
            background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
            height="48px"
            maxWidth="100%"
            variant="contained"
            width="220px"
            borderRadius="100px"
          >
            Adicionar pergunta
          </DashboardhMediumButton>
        )}
      </SectionHeaderRow>
      <SectionDescription>
        Adicione à avaliação do evento uma pergunta própria, com opções de
        resposta pré-definidas. Cada evento aceita no máximo 1 pergunta extra,
        e as opções aparecem para o comprador na ordem definida aqui.
      </SectionDescription>

      {isLoading && (
        <ExceptionContainer height={120}>
          <Loader />
        </ExceptionContainer>
      )}
      {!isLoading && loadError && (
        <EmptyState>Não foi possível carregar a pergunta extra.</EmptyState>
      )}
      {!isLoading && !loadError && isEditing && renderForm()}
      {!isLoading && !loadError && !isEditing && question && renderSummary()}
      {!isLoading && !loadError && !isEditing && !question && (
        <EmptyState>Nenhuma pergunta extra configurada para este evento.</EmptyState>
      )}
    </>
  )
}
